/**
 * TỔNG KHO DỰ ÁN (thêm 22/09/2026): mỗi dự án có TỒN BAN ĐẦU (video, thumbnail) ghi trên chính bản ghi
 * dự án, và SẢN LƯỢNG nhập theo TUẦN — mỗi tuần một bản ghi `projwk` nối bằng `pj`. Kho hiện tại = tồn
 * ban đầu + tổng sản lượng. Hàm thuần, có test.
 */
import type { RecordDto } from '@halo/shared';
import { toCsv } from './csv';
import { dateOrdinal, parseFullDate, type WeekColumn } from './dates';
import { compactData, readString, type RecordData } from './records/parsing';
import { PROJECT_STATUS_LABEL, projectLead, readStock, type Project } from './records/project';

export interface ProjectWeek {
  recordId: number;
  projectId: number;
  /** «DD/MM/YYYY» của ngày Chủ nhật — cùng khoá với `recentWeeks`. */
  weekKey: string;
  video: number;
  thumb: number;
  /** Người nhập lần cuối, và lúc nào (ISO) — để xem lịch sử. */
  by: string;
  at: string;
}

export function parseProjectWeek(record: RecordDto): ProjectWeek {
  const data = record.data;
  return {
    recordId: record.id,
    projectId: readStock(data, 'pj'),
    weekKey: readString(data, 'w'),
    video: readStock(data, 'v'),
    thumb: readStock(data, 't'),
    by: readString(data, 'by'),
    at: readString(data, 'at'),
  };
}

export function serializeProjectWeek(week: Omit<ProjectWeek, 'recordId'>): RecordData {
  return compactData({
    pj: week.projectId,
    w: week.weekKey,
    v: week.video,
    t: week.thumb,
    by: week.by,
    at: week.at,
  });
}

export interface InventoryRow {
  project: Project;
  producedVideo: number;
  producedThumb: number;
  /** Kho hiện tại = tồn ban đầu + sản lượng. */
  stockVideo: number;
  stockThumb: number;
  /** Các tuần đã nhập của dự án, tuần mới nhất trước. */
  weeks: ProjectWeek[];
}

const weekOrdinal = (week: ProjectWeek) => {
  const date = parseFullDate(week.weekKey);
  return date ? dateOrdinal(date) : 0;
};

/** Một tuần có hai bản ghi (nhập trùng tay) thì bản ghi mới hơn (id lớn hơn) là số đúng. */
function latestPerWeek(weeks: readonly ProjectWeek[]): ProjectWeek[] {
  const byKey = new Map<string, ProjectWeek>();
  for (const week of weeks) {
    const current = byKey.get(week.weekKey);
    if (!current || current.recordId < week.recordId) byKey.set(week.weekKey, week);
  }
  return [...byKey.values()].sort((a, b) => weekOrdinal(b) - weekOrdinal(a));
}

export function buildInventory(
  projects: readonly Project[],
  weeks: readonly ProjectWeek[],
): InventoryRow[] {
  const byProject = new Map<number, ProjectWeek[]>();
  for (const week of weeks) {
    byProject.set(week.projectId, [...(byProject.get(week.projectId) ?? []), week]);
  }
  return projects.map((project) => {
    const own = latestPerWeek(byProject.get(project.recordId) ?? []);
    const producedVideo = own.reduce((sum, week) => sum + week.video, 0);
    const producedThumb = own.reduce((sum, week) => sum + week.thumb, 0);
    return {
      project,
      producedVideo,
      producedThumb,
      stockVideo: project.initialVideo + producedVideo,
      stockThumb: project.initialThumb + producedThumb,
      weeks: own,
    };
  });
}

export interface InventorySummary {
  projects: number;
  stockVideo: number;
  stockThumb: number;
  producedVideo: number;
  producedThumb: number;
}

export function summarizeInventory(rows: readonly InventoryRow[]): InventorySummary {
  return rows.reduce<InventorySummary>(
    (sum, row) => ({
      projects: sum.projects + 1,
      stockVideo: sum.stockVideo + row.stockVideo,
      stockThumb: sum.stockThumb + row.stockThumb,
      producedVideo: sum.producedVideo + row.producedVideo,
      producedThumb: sum.producedThumb + row.producedThumb,
    }),
    { projects: 0, stockVideo: 0, stockThumb: 0, producedVideo: 0, producedThumb: 0 },
  );
}

export function findWeek(row: InventoryRow, weekKey: string): ProjectWeek | undefined {
  return row.weeks.find((week) => week.weekKey === weekKey);
}

export interface WeekTotal {
  key: string;
  rangeLabel: string;
  video: number;
  thumb: number;
}

/** Tổng sản lượng của các dự án đang hiện theo từng cột tuần (biểu đồ + dòng TỔNG). */
export function weekTotals(
  rows: readonly InventoryRow[],
  weeks: readonly WeekColumn[],
): WeekTotal[] {
  return weeks.map((week) => {
    let video = 0;
    let thumb = 0;
    for (const row of rows) {
      const entry = findWeek(row, week.key);
      if (entry) {
        video += entry.video;
        thumb += entry.thumb;
      }
    }
    return { key: week.key, rangeLabel: week.rangeLabel, video, thumb };
  });
}

/** Bảng tổng kho thành CSV (mở bằng Excel): số để nguyên, không định dạng nghìn. */
export function inventoryCsv(
  rows: readonly InventoryRow[],
  personName: (username: string) => string,
): string {
  return toCsv([
    [
      'Dự án',
      'Mã',
      'Người phụ trách',
      'Trạng thái',
      'Tồn ban đầu video',
      'Tồn ban đầu thumbnail',
      'Đã sản xuất video',
      'Đã sản xuất thumbnail',
      'Kho hiện tại video',
      'Kho hiện tại thumbnail',
    ],
    ...rows.map((row) => [
      row.project.title,
      row.project.code,
      personName(projectLead(row.project)),
      PROJECT_STATUS_LABEL[row.project.status],
      String(row.project.initialVideo),
      String(row.project.initialThumb),
      String(row.producedVideo),
      String(row.producedThumb),
      String(row.stockVideo),
      String(row.stockThumb),
    ]),
  ]);
}
